import { useState } from 'react';
import axios from 'axios';

const useSendEmail = ({ authenticatedAxios, getAccessTokenSilently, showToast, user }) => {
  // Send Email modal state
  const [isSendEmailModalVisible, setIsSendEmailModalVisible] = useState(false);
  const [sendEmailRecipient, setSendEmailRecipient] = useState(null); // { name, email, threadId }
  const [sendEmailForm, setSendEmailForm] = useState({
    templateType: 'manualLeadInvite',
    subject: '',
    personalMessage: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendEmailStatus, setSendEmailStatus] = useState(null); // { success: boolean, message: string }

  // Signature templates state
  const [signatureTemplates, setSignatureTemplates] = useState([]);
  const [selectedSignatureTemplates, setSelectedSignatureTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [docuSignConnected, setDocuSignConnected] = useState(false);

  // Open send email modal
  const openSendEmailModal = async (threadId, setIsPopupVisible) => {
    if (!threadId) {
      showToast('Could not find conversation', 'error');
      return;
    }

    setIsPopupVisible(false);

    try {
      // Fetch full conversation details to get the user with email
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Find the user in the conversation (same logic as showUserInfo)
      const filteredUsers = response.data.users ? response.data.users.filter((user_conv) => {
        return user_conv && user_conv.id && user_conv.id !== user.sub && user_conv.userName !== "GPT";
      }) : [];

      let targetUser = filteredUsers[0];
      if (!targetUser && response.data.users) {
        targetUser = response.data.users.find(u => u.id !== user.sub) || response.data.users[0];
      }

      if (!targetUser) {
        showToast('Could not find user in conversation', 'error');
        return;
      }

      // Get email from the user object
      const userEmail = targetUser.email || targetUser.userEmail || targetUser.mail;
      
      if (!userEmail) {
        showToast('This user does not have an email address', 'error');
        return;
      }

      setSendEmailRecipient({
        name: targetUser.userName || targetUser.name || targetUser.full_name || targetUser.fullName || 'User',
        email: userEmail,
        threadId: threadId
      });
      
      // Reset form
      setSendEmailForm({
        templateType: 'manualLeadInvite',
        subject: '',
        personalMessage: ''
      });
      setSendEmailStatus(null);
      setSelectedSignatureTemplates([]);
      setIsSendEmailModalVisible(true);

      // Fetch signature templates and DocuSign status in parallel
      setLoadingTemplates(true);
      try {
        const [templatesRes, docuSignRes] = await Promise.all([
          authenticatedAxios.get('/signature-templates'),
          authenticatedAxios.get('/integrations/docusign/status')
        ]);
        setSignatureTemplates(templatesRes.data.templates || []);
        setDocuSignConnected(docuSignRes.data.connected || false);
      } catch (err) {
        console.error('Error fetching templates or DocuSign status:', err);
      } finally {
        setLoadingTemplates(false);
      }

    } catch (error) {
      console.error('Error fetching user details for email:', error);
      showToast('Could not load user information', 'error');
    }
  };

  // Handle send email form change
  const handleSendEmailFormChange = (field, value) => {
    setSendEmailForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle signature template selection
  const toggleSignatureTemplate = (templateId) => {
    setSelectedSignatureTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  // Send email to lead
  const handleSendEmail = async () => {
    if (isSendingEmail || !sendEmailRecipient) return;

    // Validate required fields based on template
    if (!sendEmailForm.personalMessage.trim() && sendEmailForm.templateType === 'customMessage') {
      setSendEmailStatus({ success: false, message: 'Please enter a message' });
      return;
    }

    // Validate DocuSign connection if signature templates are selected
    if (selectedSignatureTemplates.length > 0 && !docuSignConnected) {
      setSendEmailStatus({ success: false, message: 'Please connect DocuSign in Profile Settings to send documents for signature' });
      return;
    }

    setIsSendingEmail(true);
    setSendEmailStatus(null);

    try {
      const response = await authenticatedAxios.post('/api/email/send-to-lead', {
        email: sendEmailRecipient.email,
        leadName: sendEmailRecipient.name,
        templateType: sendEmailForm.templateType,
        subject: sendEmailForm.subject || undefined,
        personalMessage: sendEmailForm.personalMessage || undefined,
        signatureTemplateIds: selectedSignatureTemplates.length > 0 ? selectedSignatureTemplates : undefined
      });

      if (response.data?.success) {
        const message = selectedSignatureTemplates.length > 0 
          ? 'Email sent and document sent for signature!'
          : 'Email sent successfully!';
        setSendEmailStatus({ success: true, message });
        showToast(`Email sent to ${sendEmailRecipient.email}`, 'success');
        
        // Close modal after a short delay
        setTimeout(() => {
          setIsSendEmailModalVisible(false);
        }, 1500);
      } else {
        setSendEmailStatus({ success: false, message: response.data?.error || 'Failed to send email' });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSendEmailStatus({ 
        success: false, 
        message: error.response?.data?.error || 'Failed to send email. Please try again.' 
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Close send email modal
  const closeSendEmailModal = () => {
    setIsSendEmailModalVisible(false);
    setSendEmailRecipient(null);
    setSendEmailStatus(null);
  };

  return {
    isSendEmailModalVisible,
    setIsSendEmailModalVisible,
    sendEmailRecipient,
    setSendEmailRecipient,
    sendEmailForm,
    setSendEmailForm,
    isSendingEmail,
    setIsSendingEmail,
    sendEmailStatus,
    setSendEmailStatus,
    signatureTemplates,
    setSignatureTemplates,
    selectedSignatureTemplates,
    setSelectedSignatureTemplates,
    loadingTemplates,
    setLoadingTemplates,
    docuSignConnected,
    setDocuSignConnected,
    openSendEmailModal,
    handleSendEmailFormChange,
    toggleSignatureTemplate,
    handleSendEmail,
    closeSendEmailModal,
  };
};

export default useSendEmail;
